import { useState } from 'react';
import { useFormik } from 'formik';
import { CountryData } from 'react-intl-tel-input';
import { OptionType } from '@withjoy/joykit';
import * as Yup from 'yup';
import { GetRegistryShippingAddressDocument, useSetRegistryAddressMutation } from '@graphql/generated';
import { AddressDialogFields } from '@apps/registry/common/components/AddressDialog/types';
import { NATIONAL_COUNTRY, countryList, NATIONAL_COUNTRY_CODE } from './constants';
import { ShippingDialogView } from './ShippingAddress';
import { useShippingAddressTelemetry } from './ShippingAddress.telemetry';
export const e164Regex = /^\+{0,1}\d{0,15}$/;

const initialValues: AddressDialogFields = {
  name: '',
  phone: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  state: '',
  country: NATIONAL_COUNTRY,
  countryCode: NATIONAL_COUNTRY_CODE,
  validated: false
};

const validationSchema = Yup.object<AddressDialogFields>({
  name: Yup.string().required('Required'),
  phone: Yup.string(),
  address1: Yup.string().required('Required'),
  address2: Yup.string(),
  postalCode: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
  country: Yup.string(),
  countryCode: Yup.string(),
  validated: Yup.boolean()
});

export const useFormController = ({
  eventId,
  eventHandle,
  setView,
  onCloseDialog
}: {
  eventId: string;
  eventHandle: string;
  setView: (view: ShippingDialogView) => void;
  onCloseDialog: () => void;
}) => {
  const telemetry = useShippingAddressTelemetry();
  const [setRegistryAddress] = useSetRegistryAddressMutation({
    refetchQueries: [{ query: GetRegistryShippingAddressDocument, variables: { eventHandle } }]
  });
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(false);
  const [e164PhoneNumber, setE164PhoneNumber] = useState<string>('');
  const [phoneNumberFocused, setPhoneNumberFocused] = useState<boolean>(false);

  const onSaveFormAddress = async (shippingAddressFields: AddressDialogFields) => {
    telemetry.saveAddressClicked();
    await setRegistryAddress({
      variables: {
        eventId,
        payload: {
          name: shippingAddressFields.name || '',
          phone: shippingAddressFields.phone || '',
          address1: shippingAddressFields.address1,
          address2: shippingAddressFields.address2 || '',
          city: shippingAddressFields.city,
          state: shippingAddressFields.state,
          postalCode: shippingAddressFields.postalCode || '',
          country: shippingAddressFields.country || '',
          countryCode: shippingAddressFields.countryCode || null,
          validated: shippingAddressFields.validated || false
        }
      }
    });
  };

  const formik = useFormik<AddressDialogFields>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async values => {
      try {
        const mutationData: AddressDialogFields = {
          name: values.name,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          country: values.country,
          state: values.state,
          countryCode: values.countryCode,
          postalCode: values.postalCode,
          validated: values.validated,
          phone: values.phone
        };

        await onSaveFormAddress(mutationData);
        onCloseDialog();
      } catch (error) {}
    }
  });

  const getFormFieldError = (fieldName: keyof AddressDialogFields) => {
    const { errors, touched } = formik;
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined;
  };

  const handleOnCountryDropdownChange = (option: OptionType | null): void => {
    if (!option) return;

    formik.setFieldValue('country', option.label);
    formik.setFieldValue('countryCode', option.value);
  };

  const handleOnPhoneNumberChange = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string): void => {
    if (!e164Regex.test(value)) {
      return;
    }
    setIsPhoneNumberValid(isValid); //?.includes(selectedCountryData?.iso2 ?? '');
    formik.setFieldValue('phone', value);
    // remove empty spaces and - to convert to e164 format
    setE164PhoneNumber(fullNumber.replace(/ |-/g, '').trim());
  };

  const handleOnPhoneNumberFocus = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string): void => {
    if (!phoneNumberFocused) {
      setPhoneNumberFocused(true);
    }
    handleOnPhoneNumberChange(isValid, value, selectedCountryData, fullNumber, extension);
  };

  const handleOValidateAndSubmitAddress = () => {
    setView('verify');
  };

  return {
    getFieldProps: formik.getFieldProps,
    isFormValid: formik.isValid,
    errors: {
      firstNameError: getFormFieldError('name'),
      address1Error: getFormFieldError('address1'),
      postalCodeError: getFormFieldError('postalCode'),
      cityError: getFormFieldError('city'),
      regionError: getFormFieldError('state'),
      countryError: getFormFieldError('country')
    },
    countryList,
    handleOnCountryDropdownChange,
    formik,
    handleOnPhoneNumberFocus,
    isPhoneNumberValid,
    e164PhoneNumber,
    handleOnPhoneNumberChange,
    isPhoneNumberInvalid: !!formik.values.phone && phoneNumberFocused && !isPhoneNumberValid,
    handleOValidateAndSubmitAddress
  };
};
